import api from '@/lib/api';
import type {
  ApiResponse,
  ArtifactDetail,
  EdmArtifact,
  EdmLayout,
  VersionDetail,
  VersionListItem,
} from '@/artifactEditor/types/edm';

const BASE = '/learning-tutorials/edm';

function unwrap<T>(res: { data: ApiResponse<T> }): T {
  const body = res.data;
  if (!body.success || body.data === undefined) {
    const msg = body.error?.message || 'Request failed';
    throw new Error(msg);
  }
  return body.data;
}

export async function fetchArtifact(artifactId: string): Promise<ArtifactDetail> {
  const res = await api.get<ApiResponse<ArtifactDetail>>(`${BASE}/artifacts/${artifactId}`);
  return unwrap(res);
}

export async function saveArtifactGraph(
  artifactId: string,
  payload: { expected_version: number; edm: EdmArtifact; change_summary?: string },
): Promise<ArtifactDetail> {
  const res = await api.post<ApiResponse<ArtifactDetail>>(
    `${BASE}/artifacts/${artifactId}/save`,
    payload,
  );
  return unwrap(res);
}

export async function updateArtifactMetadata(
  artifactId: string,
  payload: {
    expected_version: number;
    name?: string;
    description?: string;
    change_summary?: string;
  },
): Promise<ArtifactDetail> {
  const res = await api.put<ApiResponse<ArtifactDetail>>(
    `${BASE}/artifacts/${artifactId}`,
    payload,
  );
  return unwrap(res);
}

export async function applyLayout(
  artifactId: string,
  payload: {
    expected_version: number;
    layout: Record<string, unknown>;
    change_summary?: string;
  },
): Promise<ArtifactDetail> {
  const res = await api.post<ApiResponse<ArtifactDetail>>(
    `${BASE}/artifacts/${artifactId}/layout`,
    payload,
  );
  return unwrap(res);
}

export interface LayoutApplyResult {
  artifact_id: string;
  edm: EdmArtifact;
  current_version: number;
  latest_version: number;
  metrics: Record<string, unknown>;
  react_flow?: { nodes: unknown[]; edges: unknown[] };
  viewport?: { x: number; y: number; zoom: number; padding?: number };
}

export interface LayoutPreviewResult {
  artifact_id: string;
  edm: EdmArtifact;
  metrics: Record<string, unknown>;
  react_flow?: { nodes: unknown[]; edges: unknown[] };
  viewport?: { x: number; y: number; zoom: number; padding?: number };
  edge_routing?: Record<string, Array<{ x: number; y: number }>>;
}

export async function previewLayoutCompute(
  artifactId: string,
  payload: {
    layout: EdmLayout | Record<string, unknown>;
    edm?: EdmArtifact;
  },
): Promise<LayoutPreviewResult> {
  const res = await api.post<ApiResponse<LayoutPreviewResult>>(
    `${BASE}/artifacts/${artifactId}/layout/preview`,
    payload,
  );
  return unwrap(res);
}

export async function applyLayoutCompute(
  artifactId: string,
  payload: {
    expected_version: number;
    layout: EdmLayout | Record<string, unknown>;
    change_summary?: string;
    edm?: EdmArtifact;
  },
): Promise<LayoutApplyResult> {
  const res = await api.post<ApiResponse<LayoutApplyResult>>(
    `${BASE}/artifacts/${artifactId}/layout/apply`,
    payload,
  );
  return unwrap(res);
}

export async function listVersions(artifactId: string): Promise<VersionListItem[]> {
  const res = await api.get<ApiResponse<VersionListItem[]>>(
    `${BASE}/artifacts/${artifactId}/versions`,
  );
  return unwrap(res);
}

export async function fetchVersion(
  artifactId: string,
  versionNumber: number,
): Promise<VersionDetail> {
  const res = await api.get<ApiResponse<VersionDetail>>(
    `${BASE}/artifacts/${artifactId}/versions/${versionNumber}`,
  );
  return unwrap(res);
}

export async function restoreVersion(
  artifactId: string,
  versionNumber: number,
  payload: { expected_version: number; change_summary?: string },
): Promise<ArtifactDetail> {
  const res = await api.post<ApiResponse<ArtifactDetail>>(
    `${BASE}/artifacts/${artifactId}/versions/${versionNumber}/restore`,
    payload,
  );
  return unwrap(res);
}

export async function createCheckpoint(
  artifactId: string,
  payload: { expected_version: number; change_summary?: string },
): Promise<ArtifactDetail> {
  const res = await api.post<ApiResponse<ArtifactDetail>>(
    `${BASE}/artifacts/${artifactId}/versions`,
    payload,
  );
  return unwrap(res);
}

export async function fetchArtifactSvg(
  artifactId: string,
  params?: { theme?: string; export_mode?: string },
): Promise<string> {
  const res = await api.get<string>(`${BASE}/artifacts/${artifactId}/render/svg`, {
    params: {
      theme: params?.theme ?? 'educational',
      export_mode: params?.export_mode ?? 'svg',
    },
    responseType: 'text',
  });
  return typeof res.data === 'string' ? res.data : String(res.data ?? '');
}
